import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import prompts, { categories, difficulties } from '../data/prompts';
import { ArrowRight, SearchIcon } from '../components/Icons';

function DifficultyBadge({ level }) {
  const colors = {
    beginner: '#34d399',
    intermediate: '#fbbf24',
    advanced: '#f472b6',
  };
  return (
    <span
      className="prompt-difficulty"
      style={{
        color: colors[level],
        borderColor: colors[level] + '40',
        background: colors[level] + '10',
      }}
    >
      {level}
    </span>
  );
}

function PromptCard({ prompt, index }) {
  const navigate = useNavigate();
  const cat = categories.find((c) => c.id === prompt.category);

  return (
    <div
      className={`prompt-card page-enter stagger-${(index % 7) + 1}`}
      onClick={() => navigate(`/prompts/${prompt.id}`)}
    >
      <div className="card-accent" style={{ background: cat?.color }} />
      <div className="prompt-card-header">
        <span
          className="prompt-category-tag"
          style={{
            color: cat?.color,
            borderColor: cat?.color + '40',
            background: cat?.color + '10',
          }}
        >
          {cat?.label}
        </span>
        <DifficultyBadge level={prompt.difficulty} />
      </div>
      <h3>{prompt.title}</h3>
      <p>{prompt.description}</p>
      <div className="prompt-card-tags">
        {prompt.tags.slice(0, 4).map((tag) => (
          <span key={tag} className="prompt-tag">
            {tag}
          </span>
        ))}
        {prompt.tags.length > 4 && (
          <span className="prompt-tag prompt-tag-more">+{prompt.tags.length - 4}</span>
        )}
      </div>
      <div className="card-arrow">
        <ArrowRight />
      </div>
    </div>
  );
}

export default function PromptLibraryPage() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeDifficulty, setActiveDifficulty] = useState('all');

  const filtered = useMemo(() => {
    return prompts.filter((p) => {
      // Category filter
      if (activeCategory !== 'all' && p.category !== activeCategory) return false;
      // Difficulty filter
      if (activeDifficulty !== 'all' && p.difficulty !== activeDifficulty) return false;
      // Search
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          p.title.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.tags.some((t) => t.includes(q)) ||
          p.category.includes(q)
        );
      }
      return true;
    });
  }, [search, activeCategory, activeDifficulty]);

  const resultCount = filtered.length;
  const totalCount = prompts.length;

  return (
    <div className="page-enter">
      <div className="detail-header">
        <div className="hero-badge">Prompt Engineering</div>
        <h1>
          Prompt <span style={{ color: 'var(--blue)' }}>Library</span>
        </h1>
        <div className="subtitle">
          Production-ready prompt templates for DevOps workflows. Copy, customize, and use.
        </div>
      </div>

      {/* Search */}
      <div className="prompt-search-wrap">
        <div className="prompt-search">
          <SearchIcon />
          <input
            type="text"
            placeholder="Search prompts by name, tag, or keyword..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="prompt-search-clear" onClick={() => setSearch('')}>
              &times;
            </button>
          )}
        </div>
        <div className="prompt-search-count">
          {resultCount === totalCount
            ? `${totalCount} prompts`
            : `${resultCount} of ${totalCount} prompts`}
        </div>
      </div>

      {/* Category filter */}
      <div className="prompt-filters">
        <div className="prompt-filter-group">
          <span className="prompt-filter-label">Category</span>
          <div className="prompt-filter-chips">
            <button
              className={`prompt-chip ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => setActiveCategory('all')}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`prompt-chip ${activeCategory === cat.id ? 'active' : ''}`}
                style={
                  activeCategory === cat.id
                    ? { borderColor: cat.color + '60', background: cat.color + '15', color: cat.color }
                    : {}
                }
                onClick={() => setActiveCategory(cat.id === activeCategory ? 'all' : cat.id)}
              >
                <span className="dot" style={{ background: cat.color }} />
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        <div className="prompt-filter-group">
          <span className="prompt-filter-label">Difficulty</span>
          <div className="prompt-filter-chips">
            <button
              className={`prompt-chip ${activeDifficulty === 'all' ? 'active' : ''}`}
              onClick={() => setActiveDifficulty('all')}
            >
              All
            </button>
            {difficulties.map((d) => {
              const colors = { beginner: '#34d399', intermediate: '#fbbf24', advanced: '#f472b6' };
              return (
                <button
                  key={d}
                  className={`prompt-chip ${activeDifficulty === d ? 'active' : ''}`}
                  style={
                    activeDifficulty === d
                      ? { borderColor: colors[d] + '60', background: colors[d] + '15', color: colors[d] }
                      : {}
                  }
                  onClick={() => setActiveDifficulty(d === activeDifficulty ? 'all' : d)}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <div className="prompt-empty">
          <p>No prompts match your filters.</p>
          <button
            className="prompt-chip active"
            onClick={() => {
              setSearch('');
              setActiveCategory('all');
              setActiveDifficulty('all');
            }}
          >
            Clear all filters
          </button>
        </div>
      ) : (
        <div className="prompt-grid">
          {filtered.map((p, i) => (
            <PromptCard key={p.id} prompt={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
